import type { EstadoDelContexto } from "@/lib/informes";

/**
 * Cómo se lee en pantalla la versión en Markdown de un archivo — la que lee
 * la IA.
 *
 * ══ POR QUÉ ES UN MÓDULO APARTE Y NO TEXTO DENTRO DEL COMPONENTE ════════════
 *
 * Es la única lógica de esta entrega que puede estar mal sin que nada se vea
 * roto: un motivo mapeado al consejo equivocado imprime una frase perfecta que
 * manda a pedirle al cliente algo que no corresponde. Dentro del árbol, un
 * test no distingue un mapa bueno de uno cruzado. Es el mismo criterio con el
 * que la agrupación de plantaciones vive en `plantaciones.ts`.
 *
 * ══ POR QUÉ LOS TEXTOS VIVEN ACÁ Y NO EN EL ENUM DE PHP ═════════════════════
 *
 * `InformeResource` manda el CÓDIGO y no la etiqueta a propósito: cuánto
 * espacio hay para el rótulo se sabe de este lado. `EstadoDelContexto` tenía
 * un `etiqueta()` que no llamaba nadie y que, con estos textos escritos, sería
 * un segundo lugar declarando lo mismo sin nada que los mantenga iguales — se
 * retiró en esta misma entrega.
 */

/** Cómo se pinta el estado: son las variantes semánticas del DS. */
export type VarianteDelContexto = "success" | "info" | "warning" | "outline";

export interface LecturaDelContexto {
  etiqueta: string;
  variante: VarianteDelContexto;
  /**
   * Qué pasó y qué hacer, o `null` cuando no hay nada que hacer.
   *
   * Solo un FALLO tiene detalle: es lo único accionable de toda la cadena
   * —"pídele al cliente la versión sin contraseña"— y lo único que la persona
   * no puede deducir mirando el archivo.
   */
  detalle: string | null;
}

/**
 * Qué hacer ante cada motivo que el servidor puede guardar en
 * `contexto_motivo`.
 *
 * Son NUEVE: seis que traduce `ConversorDeDocumentos::CODIGOS_DEL_DOCUMENTO`,
 * uno que escribe `TranscriptorDeEscaneos` por FORMATO, y dos que escriben los
 * `failed()` de `ConvertirElArchivoAContexto` y `TranscribirElArchivoEscaneado`
 * cuando se agotan los TRES reintentos de la cola
 * (`odd/tasks/transcripcion-en-limbo.md`). Cada frase dice una acción
 * distinta, porque si dos códigos llevaran al mismo consejo no harían falta
 * dos códigos.
 *
 * NINGUNA MENCIONA UN NÚMERO DE MEGAS, y no es por brevedad: los dos techos
 * viven en constantes de PHP (48 MB en el conversor, 12 en el transcriptor) y
 * escribirlos acá sería un tercer lugar que se desincroniza en silencio. Lo
 * que la persona necesita —"pide una versión más liviana"— no cambia con el
 * número.
 */
const QUE_HACER: Record<string, string> = {
  cifrado:
    "El archivo está protegido con contraseña. Pídele al cliente la versión sin proteger.",
  malformado: "El archivo llegó dañado o incompleto. Pide que lo vuelvan a enviar.",
  no_soportado:
    "Ese formato no se puede leer. Sirven PDF, Word, Excel, PowerPoint y las imágenes.",
  limite_de_recursos:
    "El documento es demasiado complejo para procesarlo entero. Si se puede, pídelo dividido.",
  parte_faltante:
    "Al archivo le falta una de sus partes internas: llegó a medias. Pide que lo vuelvan a enviar.",
  // Solo alcanzable si algún día el expediente admite archivos más pesados que
  // el techo del conversor: hoy la subida corta en 32 MB y el conversor en 48.
  demasiado_grande: "El archivo pesa más de lo que el conversor puede procesar.",
  // ESTA SÍ PASA HOY, y es la única que necesita explicar una contradicción: el
  // expediente aceptó el archivo y aun así no se puede leer, porque el techo
  // para transcribir es más bajo que el de la subida.
  demasiado_grande_para_la_ia:
    "El escaneo pesa más de lo que se puede transcribir, aunque el expediente lo haya "
    + "aceptado. Pide una versión más liviana o divídelo en partes.",
  // LAS DOS DE ABAJO son la única razón por la que existe el botón
  // "Reintentar": la cola agotó sus tres intentos sin que el problema fuera el
  // ARCHIVO —un proveedor caído, un timeout— así que lo que corresponde no es
  // pedirle otra cosa al cliente, sino volver a intentarlo tal cual está.
  transcripcion_agotada:
    "La transcripción no respondió después de varios intentos. Puedes reintentarla; si "
    + "vuelve a fallar, avísale a soporte.",
  conversion_agotada:
    "La conversión no respondió después de varios intentos. Puedes reintentarla; si vuelve "
    + "a fallar, avísale a soporte.",
};

/** Lo que necesita `lecturaDelContexto`: un recorte del archivo, no el objeto entero. */
export interface ContextoDeArchivo {
  contexto_estado: EstadoDelContexto | null;
  contexto_motivo: string | null;
  /**
   * Si el procesamiento está EN VUELO, decidido por el SERVIDOR
   * (`InformeDocumentoArchivo::contextoEnCurso()`). Es lo único que distingue
   * "Transcribiendo"/"Procesando" de "No se pudo transcribir"/"Sin procesar"
   * para los dos estados que no son una conclusión todavía.
   */
  contexto_en_curso: boolean;
}

/**
 * Traduce el estado de un archivo a lo que la fila tiene que mostrar.
 *
 * ══ `null` Y `necesita_ocr` SE PARTEN EN DOS, SEGÚN `contexto_en_curso` ═════
 *
 * Antes "Necesita transcripción" era una etiqueta ESTÁTICA a propósito: decir
 * "Transcribiendo" habría sido una promesa que nadie garantizaba, porque no
 * había forma de saber si la cola todavía la estaba corriendo o si ya la
 * había dado por perdida hace días. Hoy el servidor SÍ lo sabe
 * (`InformeDocumentoArchivo::contextoEnCurso()`, la misma ventana que decide
 * si ofrecer "Reintentar") y lo manda, así que decir "Transcribiendo" o
 * "Procesando" mientras esa ventana sigue abierta ya no miente. Cuando se
 * cerró sin que nada lo moviera, la fila está TRABADA y lo dice: "No se pudo
 * transcribir" en vez de seguir prometiendo un trabajo que no está corriendo.
 *
 * ══ `null` —que nadie lo intentó todavía— sigue siendo un caso REAL ═════════
 *
 * O el trabajo está en la cola, o el archivo se subió antes de que esta
 * función existiera. Dejarlo sin rótulo lo haría indistinguible de un archivo
 * ya legible, y un expediente enteramente sin procesar se vería igual que uno
 * enteramente listo.
 *
 * ══ UN CÓDIGO DESCONOCIDO NO DEJA LA PANTALLA MUDA ══════════════════════════
 *
 * Un api más nuevo que este webapp puede escribir un motivo que este mapa no
 * tiene. La etiqueta sigue diciendo que el archivo no se pudo leer —que es lo
 * que importa— y lo único que se pierde es el consejo. Inventar uno genérico
 * ("vuelve a subirlo") sería peor: manda a hacer algo que puede no servir.
 */
export function lecturaDelContexto({
  contexto_estado: estado,
  contexto_motivo: motivo,
  contexto_en_curso: enCurso,
}: ContextoDeArchivo): LecturaDelContexto {
  if (estado === "listo") {
    return { etiqueta: "Listo", variante: "success", detalle: null };
  }

  if (estado === "necesita_ocr") {
    if (enCurso) {
      return { etiqueta: "Transcribiendo", variante: "info", detalle: null };
    }

    return {
      etiqueta: "No se pudo transcribir",
      variante: "warning",
      detalle: "No se pudo completar la transcripción. Puedes reintentarla con el botón "
        + '"Reintentar".',
    };
  }

  if (estado === "fallido") {
    return {
      etiqueta: "No se pudo leer",
      variante: "warning",
      detalle: motivo === null ? null : (QUE_HACER[motivo] ?? null),
    };
  }

  // estado === null: nadie lo intentó todavía, o la cola lo está por tomar.
  if (enCurso) {
    return { etiqueta: "Procesando", variante: "info", detalle: null };
  }

  // "Sin procesar" y no "Pendiente": lo segundo prometería que va a ocurrir, y
  // para los archivos anteriores a esta función no hay nadie que los recorra
  // todavía.
  return { etiqueta: "Sin procesar", variante: "outline", detalle: null };
}
